import type { PartialUser } from '@/validators/auth'
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import { useLoginMutation } from '@/api/auth'
import { apiClient } from '@/api/base'
import { AxiosError } from 'axios'
import { createContext, useContext, useState } from 'react'
import { toast } from 'sonner'

export interface AuthContext {
  user: PartialUser | null
  isAuthenticated: boolean
  setAuth: Dispatch<SetStateAction<AuthContext>>
}

const defaultAuthContext: AuthContext = {
  user: null,
  isAuthenticated: false,
  setAuth: () => {
    throw new Error('setAuth must be used within an AuthProvider')
  },
}

const authContext = createContext<AuthContext>(defaultAuthContext)

export function AuthProvider(props: PropsWithChildren) {
  const [auth, setAuth] = useState<AuthContext>(defaultAuthContext)

  return (
    <authContext.Provider value={{ setAuth, user: auth.user, isAuthenticated: auth.isAuthenticated }}>
      {props.children}
    </authContext.Provider>
  )
}

export function useAuth() {
  const [loading, setLoading] = useState(true)
  const { mutateAsync: handleLogin } = useLoginMutation()

  const authContextObj = useContext(authContext)
  if (!authContextObj) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  async function login(email: string, password: string) {
    await handleLogin({ email, password }, {
      onError: (error) => {
        localStorage.removeItem('token')
        toast.error('Could not log you in', { description: error instanceof AxiosError ? error.response?.data.error : '' })
      },
      onSuccess: (data) => {
        localStorage.setItem('token', data.accessToken)
        authContextObj.setAuth(prev => ({ ...prev, user: data.user, isAuthenticated: true }))
      },
    })
  }

  async function logout() {
    localStorage.removeItem('token')
    authContextObj?.setAuth(prev => ({
      ...prev,
      user: defaultAuthContext.user,
      isAuthenticated: defaultAuthContext.isAuthenticated,
    }))
  }

  async function checkAuth() {
    try {
      const { data } = await apiClient('/api/auth/check-auth', { method: 'POST' })
      authContextObj.setAuth(prev => ({ ...prev, isAuthenticated: true, user: data.data.user }))
    // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      authContextObj.setAuth(prev => ({ ...prev, isAuthenticated: false, user: null }))
    } finally {
      setLoading(false)
    }
  }

  return {
    user: authContextObj.user,
    isAuthenticated: authContextObj.isAuthenticated,
    login,
    logout,
    checkAuth,
    loading,
  }
}
